import React, { useCallback, useEffect, useRef, useState } from 'react';
import QRCode from 'qrcode';
import { QRCodeSVG } from 'qrcode.react';
import { formatDate } from '../../dateFormat';

interface Props {
  templateConfig: unknown;
  programName: string;
  earningCriteria: string;
  /** Defaults to a sample name (the staff preview). */
  learnerName?: string;
  /** Shown for both dates; defaults to today. */
  dateLabel?: string;
}

const CERT_WIDTH = 1280;
const CERT_HEIGHT = 720;
const QR_SIZE = 180;

// Mirrors the server-side renderer in
// `apps/event-processors/src/services/certificate-image.service.ts` (same QR
// library, same rules): `<qrcode inline/>` draws the QR at the tag with its
// color/fill hex colours (black on white by default, invalid values fall back
// to the default); without an inline tag any `<qrcode/>` is removed and the
// legacy fixed overlay is drawn.
const QR_TAG = /<qrcode\b([^>]*?)\/?>/gi;
const HEX_COLOR = /^#?([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i;

const toHexColor = (value: string | undefined, fallback: string) =>
  value && HEX_COLOR.test(value) ? `#${value.replace(/^#/, '')}` : fallback;

const parseQrTag = (attributes: string) => {
  const attrs = Object.fromEntries(
    [...attributes.matchAll(/(\w+)="([^"]*)"/g)].map(([, k, v]) => [k, v])
  );
  return {
    inline: /\binline\b/.test(attributes.replace(/"[^"]*"/g, '')),
    dark: toHexColor(attrs.color, '#000000'),
    light: toHexColor(attrs.fill, '#FFFFFF'),
  };
};

const parseQrTags = (html: string) =>
  [...html.matchAll(QR_TAG)].map(([, attributes]) => parseQrTag(attributes));

const renderInlineQrTags = async (
  html: string,
  value: string
): Promise<string> => {
  const svgs = await Promise.all(
    parseQrTags(html).map((tag) =>
      tag.inline
        ? QRCode.toString(value, {
            type: 'svg',
            width: QR_SIZE,
            margin: 0,
            color: { dark: tag.dark, light: tag.light },
            errorCorrectionLevel: 'M',
          })
        : ''
    )
  );
  let index = 0;
  return html.replace(QR_TAG, () => svgs[index++]);
};

const escapeHtml = (str: string) =>
  str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const interpolateTemplate = (
  html: string,
  vars: Record<string, string>
): string => {
  let result = html;
  for (const [key, value] of Object.entries(vars)) {
    result = result.replace(new RegExp(`{{${key}}}`, 'g'), escapeHtml(value));
  }
  return result;
};

export const CertificateTemplatePreview: React.FC<Props> = ({
  templateConfig,
  programName,
  earningCriteria,
  learnerName = 'Jane Doe',
  dateLabel,
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0.5);
  const [inlineHtml, setInlineHtml] = useState<string | null>(null);

  const updateScale = useCallback(() => {
    if (wrapperRef.current) {
      const availableWidth = wrapperRef.current.offsetWidth;
      setScale(Math.min(1, availableWidth / CERT_WIDTH));
    }
  }, []);

  useEffect(() => {
    updateScale();
    window.addEventListener('resize', updateScale);
    return () => window.removeEventListener('resize', updateScale);
  }, [updateScale]);

  const config = templateConfig as { html?: string } | null;
  const date = dateLabel ?? formatDate(new Date());
  const sampleUrl = `${window.location.origin}/certificates/verify/sample-preview`;

  const interpolated = config?.html
    ? interpolateTemplate(config.html, {
        learner_full_name: learnerName,
        program_name: programName,
        earning_criteria: earningCriteria,
        issued_at: date,
        completion_date: date,
        certificate_url: sampleUrl,
        credential_id: 'sample-credential-id',
      })
    : '';
  const inline = parseQrTags(interpolated).some((tag) => tag.inline);

  // The QR library renders asynchronously, so inline QR codes are filled in
  // once ready.
  useEffect(() => {
    if (!inline) return;
    let cancelled = false;
    renderInlineQrTags(interpolated, sampleUrl).then((html) => {
      if (!cancelled) setInlineHtml(html);
    });
    return () => {
      cancelled = true;
    };
  }, [inline, interpolated, sampleUrl]);

  if (!config?.html) {
    return (
      <div className="rounded-lg border border-dashed border-border bg-muted/30 px-6 py-12 text-center text-sm text-muted-foreground">
        No certificate template configured. Set the template HTML in the
        templateConfig to enable certificate rendering.
      </div>
    );
  }

  const scaledHeight = CERT_HEIGHT * scale;

  return (
    <div
      ref={wrapperRef}
      className="relative overflow-hidden rounded-lg border border-border bg-card"
      style={{ width: '100%', maxWidth: CERT_WIDTH, height: scaledHeight }}
    >
      <div
        className="absolute top-0 left-0"
        style={{
          width: CERT_WIDTH,
          height: CERT_HEIGHT,
          overflow: 'hidden',
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
        }}
      >
        <div
          style={{
            position: 'relative',
            width: CERT_WIDTH,
            height: CERT_HEIGHT,
          }}
        >
          <div
            dangerouslySetInnerHTML={{
              __html:
                inline && inlineHtml
                  ? inlineHtml
                  : interpolated.replace(QR_TAG, ''),
            }}
          />
          {!inline && (
            <div
              style={{
                position: 'absolute',
                top: 482,
                left: 700,
                width: QR_SIZE,
                height: QR_SIZE,
              }}
            >
              <QRCodeSVG
                value={sampleUrl}
                size={QR_SIZE}
                bgColor="#002B56"
                fgColor="#FFFFFF"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
